import { useEffect, useState } from 'react';
import { api } from './lib/api';
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { getSupportSession, setSupportSession } from './lib/api';
import { hasRole, useAuth } from './lib/auth';
import { CreateOrg, InviteAccept, Login, MfaPage } from './pages/auth';
import { ConnectGmail } from './pages/connect-gmail';
import { ThemeToggle } from './lib/theme';
import { ICONS, NavGroups } from './ui/nav';
import { TestModeBar, TestModeToggle, WorkspaceSwitcher } from './ui/workspace';
import { Approvals, PolicyApprove } from './pages/approvals';
import { AccountDetail, Accounts, Context, Icp, IcpVersion } from './pages/data';
import { CompetitorDetail, Competitors } from './pages/competitors';
import { Home } from './pages/home';
import { ImportDetail, ImportDuplicates, Imports } from './pages/imports';
import { Reports } from './pages/reports';
import { Audiences, Campaigns, Handoffs, RevenueActions } from './pages/sales';
import { CampaignFlow, CampaignNew } from './pages/campaign-new';
import { LinkedinPage } from './pages/linkedin';
import { VisitorsPage } from './pages/visitors';
import { LaunchApprove } from './pages/launch';
import { Notifications, Refer, Replies } from './pages/growth';
import { UserMenu } from './ui/user-menu';
import { PlanPicked } from './ui/plan-picked';
import { AssistantPanel } from './ui/assistant';
import { IconRail } from './ui/rail';
import { CardsSheet, PhoneBack, PhoneTabBar } from './ui/tabbar';
import { PHONE_THREAD, usePhone } from './ui/phone';
import { ViewBar } from './ui/viewbar';
import { Logo } from './ui/logo';
import { Toasts } from './ui/toasts';
import { AllWork, Launchpad } from './pages/launchpad';
import { ChatCanvas, Chats, InboxView, WorkspaceView } from './pages/hub';
import { Audit, Billing, Developers, KillSwitches, Settings } from './pages/settings';
import { IntegrationDetail, Integrations } from './pages/integrations';
import { OpsRoutes, opsMenu } from './ops/OpsApp';
import { Agency } from './pages/agency';
import { AdCampaigns, AdTestSet } from './pages/ad-campaigns';
import { Page, useApi } from './ui/kit';
import { trackPage } from './lib/track';

type Item = { to: string; label: string; show?: boolean; badge?: number; match?: string[] };

export function App() {
  const { session, me, staff, loading, signOut, supabase } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const publicPath = loc.pathname.startsWith('/invite');
  // hooks before any early return: the Inbox badge (things waiting for a person)
  const waiting = useApi<{ policies: unknown[]; actions: unknown[]; context_proposals: unknown[] }>(me && !loc.pathname.startsWith('/ops') ? '/portal/approvals' : null, { refetchMs: 60_000 });
  // phones (the new experience): a tab bar at the bottom; on a thread page the chat is the screen, the cards a sheet
  const phone = usePhone();
  const phoneRailed = phone && Boolean(me?.org.new_experience) && !loc.pathname.startsWith('/ops');
  const phoneThread = phoneRailed && PHONE_THREAD.test(loc.pathname);
  useEffect(() => { document.documentElement.classList.toggle('phone-thread', phoneThread); }, [phoneThread]);
  // the admin journey's page view (a no-op unless main.tsx started the tracker)
  useEffect(() => { trackPage(); }, [loc.pathname]);
  if (loading && !publicPath) return <p className="muted" style={{ padding: 32 }} role="status">Loading…</p>;
  if (!supabase && !loading) return <div className="auth"><h1>Outlevo</h1><p>Sign-in is not configured on this server yet.</p></div>;
  const corner = <div className="auth-theme"><ThemeToggle /></div>;
  if (!session) return <>{corner}<Routes><Route path="/invite" element={<InviteAccept />} /><Route path="*" element={<Login />} /></Routes></>;
  if (publicPath) return <>{corner}<Routes><Route path="/invite" element={<InviteAccept />} /></Routes></>;
  if (!me && !staff) return <>{corner}<Routes><Route path="/mfa" element={<MfaPage />} /><Route path="*" element={<CreateOrg />} /></Routes></>;
  // the Gmail extension's consent screen stands alone, without the app around it
  if (me && loc.pathname === '/connect/gmail') return <>{corner}<ConnectGmail /></>;

  const isOps = loc.pathname.startsWith('/ops');
  // the new experience (a per-workspace switch): one icon rail instead of the menu, Home is the launchpad, and the
  // working views sit next to the assistant (cards left, chat right). Settings live in the account menu.
  const nx = Boolean(me?.org.new_experience);
  const railed = nx && !isOps && Boolean(me);
  const threadPage = railed && (/^\/(campaigns\/(new|[0-9a-f-]{36})|chat\/[0-9a-f-]{36})$/.test(loc.pathname) || ['/work', '/chat', '/inbox'].includes(loc.pathname));
  // the customer app in a few places (as Explee): related pages are tabs of one section, rare ones sit in the account menu
  const inboxBadge = waiting.data ? waiting.data.policies.length + waiting.data.actions.length + waiting.data.context_proposals.length : 0;
  const customer: Array<{ title: string; items: Item[] }> = me && nx ? [
    // phones: the same places as the rail, in the drawer
    { title: 'Work', items: [
      { to: '/', label: 'Home' },
      { to: '/chats', label: 'Chats', match: ['/chat'] },
      { to: '/inbox', label: 'Inbox', badge: inboxBadge || undefined, match: ['/approvals', '/handoffs', '/replies'] },
      { to: '/work', label: 'All work', match: ['/campaigns'] },
      { to: '/accounts', label: 'Accounts', match: ['/import', '/actions', '/visitors'] },
      { to: '/context', label: 'Company & ICP', match: ['/icp', '/competitors'] },
      { to: '/integrations', label: 'Integrations' },
    ] },
  ] : me ? [
    { title: 'Work', items: [
      { to: '/', label: nx ? 'Home' : 'Dashboard' },
      ...(nx ? [{ to: '/work', label: 'All work', match: ['/dashboard'] }] : []),
      { to: '/approvals', label: 'Inbox', badge: inboxBadge || undefined, match: ['/handoffs', '/replies'] },
      { to: '/accounts', label: 'Accounts', match: ['/import', '/actions', '/visitors'] },
      { to: '/agency', label: 'Clients', show: Boolean(me.org.is_agency) && hasRole(me, 'owner', 'admin') },
    ] },
    { title: 'Campaigns', items: [
      { to: '/campaigns', label: 'Campaigns', show: hasRole(me, 'owner', 'admin', 'sales_approver', 'marketing_approver'), match: ['/audiences', '/ad-campaigns', '/reports'] },
      { to: '/campaigns/new', label: '+ New campaign', show: hasRole(me, 'owner', 'admin', 'sales_approver', 'marketing_approver', 'rep') },
    ] },
    { title: 'Setup', items: [
      { to: '/context', label: 'Company & ICP', match: ['/icp', '/competitors'] },
      { to: '/integrations', label: 'Integrations', show: hasRole(me, 'owner', 'admin') },
    ] },
  ] : [];

  return (
    <div className={`shell${me && !isOps ? ' with-assistant' : ''}${railed ? ' railed' : ''}`}>
      <a className="skip" href="#main">Skip to content</a>
      {railed ? <IconRail inboxBadge={inboxBadge} canIntegrate={hasRole(me!, 'owner', 'admin')} /> : null}
      {/* one landmark per name (F18 FR4): beside the rail, which is Main, this is the menu */}
      <nav className={`nav ${open ? 'open' : ''}${railed ? ' folded' : ''}`} aria-label={railed ? 'Menu' : 'Main'}>
        <div className="brand">
          <span className="brand-name"><Logo className="logo" />{isOps ? <small> Admin</small> : null}</span>
          <button type="button" className="btn menu-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>Menu</button>
        </div>
        <div className="links" onClick={() => setOpen(false)}>
          {staff ? (
            <div className="switcher" role="group" aria-label="Switch view">
              <Link to="/ops" aria-current={isOps ? 'page' : undefined} aria-label="Admin panel" title="Admin panel">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z" /><path d="M9 12l2 2 4-4" strokeLinecap="round" /></svg>
                <span>Admin</span>
              </Link>
              <Link to={me ? '/' : '/workspace/new'} aria-current={!isOps ? 'page' : undefined} aria-label="Customer dashboard" title="Customer dashboard">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></svg>
                <span>Customer</span>
              </Link>
            </div>
          ) : null}
          {!isOps && me ? <><WorkspaceSwitcher /><TestModeToggle /></> : null}
          {isOps && staff ? <OpsMenu /> : <NavGroups groups={customer.map((g) => ({ title: g.title, items: g.items.filter((i) => i.show !== false) }))} />}
          {me && !isOps ? <NavLink to="/refer" className="nav-refer"><span>Refer and earn</span><span className="um-badge">Get $29</span></NavLink> : null}
        </div>
        <UserMenu />
      </nav>
      <CardsSheet on={phoneThread}>
        {railed ? <ViewBar split={threadPage} /> : null}
        <TestModeBar />
        <SupportBanner />
        {railed && phone ? <PhoneBack /> : null}
        {!isOps ? <SectionTabs nx={nx} /> : null}
        {me && !isOps ? <AssessmentClaim canClaim={hasRole(me, 'owner', 'admin')} /> : null}
        {me && !isOps ? <PlanPicked plan={me.org.plan} canPay={hasRole(me, 'owner') && !me.org.sandbox_only} /> : null}
        {me && !isOps ? <Announcements /> : null}
        <Routes>
          <Route path="/mfa" element={<MfaPage />} />
          {me ? <>
            <Route path="/" element={nx ? <Launchpad /> : <Home />} />
            <Route path="/dashboard" element={<Home />} />
            <Route path="/work" element={<AllWork />} />
            <Route path="/chat" element={<WorkspaceView />} />
            <Route path="/chat/:threadId" element={<ChatCanvas />} />
            <Route path="/chats" element={<Chats />} />
            <Route path="/inbox" element={<InboxView />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/approvals/:id" element={<PolicyApprove />} />
            <Route path="/actions" element={<RevenueActions />} />
            <Route path="/handoffs" element={<Handoffs />} />
            <Route path="/accounts" element={<Accounts />} />
            <Route path="/accounts/:id" element={<AccountDetail />} />
            <Route path="/visitors" element={<VisitorsPage />} />
            <Route path="/import" element={<Imports />} />
            <Route path="/import/duplicates" element={<ImportDuplicates />} />
            <Route path="/import/:id" element={<ImportDetail />} />
            <Route path="/icp" element={<Icp />} />
            <Route path="/icp/versions/:id" element={<IcpVersion />} />
            <Route path="/context" element={<Context />} />
            <Route path="/competitors" element={<Competitors />} />
            <Route path="/competitors/:id" element={<CompetitorDetail />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/campaigns/new" element={<CampaignNew />} />
            <Route path="/campaigns/:id" element={<CampaignFlow />} />
            <Route path="/launch/:id" element={<LaunchApprove />} />
            <Route path="/replies" element={<Replies />} />
            <Route path="/linkedin" element={<LinkedinPage />} />
            <Route path="/refer" element={<Refer />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/audiences" element={<Audiences />} />
            <Route path="/ad-campaigns" element={<AdCampaigns />} />
            <Route path="/ad-campaigns/:id" element={<AdTestSet />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/integrations/:slug" element={<IntegrationDetail />} />
            <Route path="/developers" element={<Developers />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/agency" element={<Agency />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/kill-switches" element={<KillSwitches />} />
            <Route path="/settings" element={<Settings />} />
          </> : null}
          {staff ? <Route path="/ops/*" element={<OpsRoutes />} /> : null}
          <Route path="/workspace/new" element={<CreateOrg />} />
          {/* guessed addresses for help and the team: Help opens the support chat; the team is in Settings */}
          <Route path="/help" element={<OpenSupport />} />
          <Route path="/support" element={<OpenSupport />} />
          <Route path="/team" element={<Navigate to="/settings" replace />} />
          {/* outlevo.com's Start buttons open /signup?plan=…; signed in, they land on Home (the plan strip is there) */}
          <Route path="/signup" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          {/* an unknown page says so with a way on (it silently became Home: /team, /linkedin-tasks) */}
          <Route path="*" element={me ? <NotFound /> : <Navigate to="/ops" replace />} />
        </Routes>
      </CardsSheet>
      {me && !isOps ? <AssistantPanel waitForAsk={nx && loc.pathname === '/'} pinned={threadPage} phoneMain={railed && phoneThread} /> : null}
      {railed && phone && !phoneThread ? <PhoneTabBar inboxBadge={inboxBadge} /> : null}
      {me && !isOps ? <Toasts /> : null}
    </div>
  );
}

function OpsMenu() {
  const { staff } = useAuth();
  const counts = useApi<Record<string, number>>('/ops/counts', { refetchMs: 60_000 });
  return <NavGroups firstOnly groups={opsMenu(staff!.permissions, counts.data ?? {})} />;
}

function SupportBanner() {
  const id = getSupportSession();
  const s = useApi<{ sessions: Array<{ id: string; org: string; org_id: string; ends_at: string; ended_at: string | null; mode?: 'read' | 'full'; acting_as?: string | null }> }>(id ? '/ops/support' : null, { refetchMs: 30_000 });
  const [ending, setEnding] = useState(false);
  const cur = s.data?.sessions.find((x) => x.id === id);
  const over = Boolean(s.data && (!cur || cur.ended_at || new Date(cur.ends_at).getTime() <= Date.now()));
  // a session that ended elsewhere (revoked, timed out, ended from another tab) stops sending the header
  useEffect(() => { if (id && over) setSupportSession(null); }, [id, over]);
  if (!id || over) return null;
  const mins = cur ? Math.max(0, Math.round((new Date(cur.ends_at).getTime() - Date.now()) / 60_000)) : null;
  const end = async () => {
    setEnding(true);
    try { await api(`/ops/support/sessions/${id}/end`, { method: 'POST', body: {} }); } catch { /* ended already */ }
    setSupportSession(null);
    location.assign('/ops/support');
  };
  return (
    <div className="banner banner-support" role="status">
      {cur?.mode === 'full'
        ? <>Signed in as <strong>{cur.acting_as ?? 'a member'}</strong> in <strong>{cur.org}</strong>, full access: changes are real{mins !== null ? `, ends in ${mins} min` : ''}.{' '}</>
        : <>Viewing <strong>{cur?.org ?? 'a customer workspace'}</strong>, read-only{mins !== null ? `, ends in ${mins} min` : ''}.{' '}</>}
      <button type="button" className="btn" onClick={() => void end()} disabled={ending}>{ending ? 'Ending…' : 'End session'}</button>
    </div>
  );
}

function Announcements() {
  const a = useApi<{ data: Array<{ id: string; title: string; body: string; link: string | null; read: boolean }> }>('/portal/announcements');
  const unread = a.data?.data.find((x) => !x.read);
  if (!unread) return null;
  return (
    <div className="banner" role="status">
      <strong>{unread.title}</strong> {unread.body} {unread.link ? <a href={unread.link} target="_blank" rel="noreferrer">More</a> : null}{' '}
      <button type="button" className="link" onClick={() => fetch(`/portal/announcements/${unread.id}/read`, { method: 'POST' }).then(() => a.refetch())}>Dismiss</button>
    </div>
  );
}

// The free assessment's link (app.outlevo.com/?assessment=<id>) survives sign-in and workspace creation; once the
// workspace exists it is claimed: the hypotheses become an ICP draft, and a card saved on the result page brings
// its free credits.
const PENDING = 'olv_assessment';
// the markets as the visitor left them on the result page (read from the website, maybe corrected): ?markets=US,CA
const PENDING_MARKETS = 'olv_assessment_markets';
const pendingFromUrl = () => {
  const q = new URLSearchParams(location.search);
  const id = q.get('assessment');
  if (id && /^[0-9a-f-]{36}$/i.test(id)) {
    try {
      localStorage.setItem(PENDING, id);
      const m = q.get('markets');
      if (m !== null && /^([A-Za-z]{2}(,[A-Za-z]{2})*)?$/.test(m)) localStorage.setItem(PENDING_MARKETS, m); else localStorage.removeItem(PENDING_MARKETS);
    } catch { /* private mode */ }
  }
};
pendingFromUrl();

function AssessmentClaim({ canClaim }: { canClaim: boolean }) {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    let id: string | null = null;
    let markets: string | null = null;
    try { id = localStorage.getItem(PENDING); markets = localStorage.getItem(PENDING_MARKETS); } catch { return; }
    if (!id || !canClaim) return;
    const forget = () => { try { localStorage.removeItem(PENDING); localStorage.removeItem(PENDING_MARKETS); } catch { /* ignore */ } };
    api<{ hypotheses?: number; markets?: string[]; free_credits?: number; already?: boolean }>(`/portal/assessments/${id}/claim`, {
      method: 'POST', body: markets === null ? {} : { markets: markets ? markets.split(',') : [] },
    })
      .then((r) => {
        forget();
        if (r.already) return;
        setMsg(`Your assessment is now an ICP draft with ${r.hypotheses ?? 0} hypotheses to review${r.markets?.length ? `, selling into ${r.markets.join(', ')}` : ''}.${r.free_credits ? ` ${r.free_credits.toLocaleString('en-US')} free credits were added.` : ''}`);
      })
      .catch((e: { status?: number }) => { if (e.status === 403 || e.status === 404) forget(); });
  }, [canClaim]);
  return msg ? <div className="banner" role="status">{msg} <a href="/icp">Open the ICP</a></div> : null;
}

// related pages share one section, shown as tabs above the page
const SECTIONS: Array<Array<[string, string]>> = [
  [['/approvals', 'Needs you'], ['/handoffs', 'Hot leads'], ['/replies', 'Replies']],
  [['/accounts', 'Accounts'], ['/visitors', 'Website visitors'], ['/import', 'Import & win/loss'], ['/actions', 'Revenue actions']],
  [['/campaigns', 'Campaigns'], ['/linkedin', 'LinkedIn tasks'], ['/audiences', 'Ad audiences'], ['/ad-campaigns', 'Ad campaigns'], ['/reports', 'Reports']],
  [['/context', 'Business context'], ['/icp', 'ICP'], ['/competitors', 'Competitors']],
  [['/settings', 'Team and account'], ['/developers', 'API keys'], ['/billing', 'Billing'], ['/notifications', 'Notifications'], ['/audit', 'Audit log'], ['/kill-switches', 'Stop spending']],
];
function SectionTabs({ nx }: { nx: boolean }) {
  const { pathname } = useLocation();
  // the new experience reaches these from the chat and the account menu; only a section's own pages keep their tabs
  if (nx && ['/approvals', '/handoffs', '/replies'].some((p) => pathname === p)) return null;
  const on = (to: string) => pathname === to || pathname.startsWith(`${to}/`);
  const section = SECTIONS.find((tabs) => tabs.some(([to]) => on(to)));
  if (!section || pathname === '/campaigns/new' || /^\/campaigns\/[0-9a-f-]{36}/.test(pathname)) return null;
  const current = section.filter(([to]) => on(to)).sort((a, b) => b[0].length - a[0].length)[0]?.[0];
  return (
    <nav className="section-tabs" aria-label="Section">
      {section.map(([to, label]) => <Link key={to} to={to} aria-current={to === current ? 'page' : undefined}>{label}</Link>)}
    </nav>
  );
}

/** /help: Home with the support chat open (the menu's Help does the same). */
function OpenSupport() {
  // not cleared on unmount: the redirect unmounts this at once, and the chat opens on Home
  useEffect(() => { setTimeout(() => window.dispatchEvent(new CustomEvent('olv-support')), 50); }, []);
  return <Navigate to="/" replace />;
}

function NotFound() {
  return (
    <Page title="Page not found">
      <section className="section">
        <p>There is no page at <code>{location.pathname}</code>.</p>
        <p><Link className="btn btn-primary" to="/">Go to Home</Link> <Link className="btn" to="/settings">Settings and team</Link> <Link className="btn" to="/integrations">Integrations</Link></p>
      </section>
    </Page>
  );
}
